import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link } from "wouter"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { api } from "@/lib/api"
import { inputClass, submitBtnClass } from "@/lib/styleClasses"

interface ForgotPasswordForm {
    email: string
}

export const AdminForgotPasswordPage = () => {
    const { t } = useTranslation()
    const [submitted, setSubmitted] = useState(false)
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotPasswordForm>()

    const onSubmit = async (data: ForgotPasswordForm) => {
        try {
            await api.post("/auth/forgot-password", data)
        } catch {
            // intentionally swallow — always show neutral success message
        } finally {
            setSubmitted(true)
        }
    }

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-2">{t("admin.forgotPassword.title")}</h1>
                    <p className="text-slate-500 font-bold">{t("admin.forgotPassword.subtitle")}</p>
                </div>

                {submitted ? (
                    <div className="text-center space-y-4">
                        <p className="text-slate-700 font-bold">{t("admin.forgotPassword.successMessage")}</p>
                        <Link to="/admin/login" className="inline-block text-sm font-black text-brand hover:text-brand-dark transition-colors">
                            {t("admin.forgotPassword.backToLogin")}
                        </Link>
                    </div>
                ) : (
                    <>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600">
                                    {t("admin.forgotPassword.email")}
                                </label>
                                <Input
                                    type="email"
                                    placeholder="admin@example.com"
                                    className={inputClass}
                                    error={errors.email?.message}
                                    {...register("email", {
                                    required: t("admin.forgotPassword.emailRequired"),
                                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("admin.forgotPassword.emailInvalid") },
                                })}
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className={submitBtnClass}
                            >
                                {isSubmitting ? t("admin.forgotPassword.sending") : t("admin.forgotPassword.send")}
                            </Button>
                        </form>

                        <p className="mt-6 text-center text-sm text-slate-500">
                            <Link to="/admin/login" className="font-black text-brand hover:text-brand-dark transition-colors">
                                {t("admin.forgotPassword.backToLogin")}
                            </Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    )
}
